import { devLog, ENV, headUserInfoStr } from '@/utils';
import { useEffect, useMemo, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { atom, useRecoilState, useSetRecoilState } from 'recoil';
import { HiMessage, HiSession } from '@/views/Chats/types';
import { useCurrentAddressInfo } from './address';
import produce from 'immer';

const WS_URL = ENV.isProduction ? 'wss://hi-ws.debank.com' : 'wss://alpha-hi-ws.debank.com';

let socketInstance: Socket | null = null;

export function useHiWebSocket(opts?: { isTopApp?: boolean }) {
  const { isVerified, address } = useCurrentAddressInfo();

  useEffect(() => {
    return () => {
      if (socketInstance) {
        socketInstance.disconnect();
        socketInstance = null;
      }
    };
  }, [address]);

  const socket = useMemo(() => {
    if (socketInstance) {
      return socketInstance;
    }
    const newSocketInstance =
      isVerified && address && headUserInfoStr
        ? io(WS_URL, {
            transports: ['websocket'],
            // In a browser environment, the extraHeaders option will be ignored if you only enable the WebSocket transport
            // https://socket.io/docs/v4/client-options/#extraheaders
            query: {
              source: 'web',
              account: headUserInfoStr,
            },
            reconnection: false,
          })
        : null;

    if (newSocketInstance) {
      socketInstance = newSocketInstance;
    }

    return newSocketInstance;
  }, [isVerified, address]);

  useEffect(() => {
    if (opts?.isTopApp && socket) {
      const onConnect = () => {
        devLog('useHiWebSocket::global::onConnect');
      };

      const onError = (error: Error) => {
        devLog('useHiWebSocket::global::onError', error);
      };

      const onDisconnect = (event: any) => {
        devLog('useHiWebSocket::global::onDisconnect', event);
      };

      const onAnyDevLog = (event: any, ...args: any) => {
        devLog('useHiWebSocket::global::onAnyDevLog', event, args);
      };

      socket.on('connect', onConnect);
      socket.on('disconnect', onDisconnect);

      socket.on('error', onError);
      socket.onAny?.(onAnyDevLog);

      return () => {
        socket.off('connect', onConnect);
        socket.off('disconnect', onDisconnect);

        socket.off('error', onError);
        socket?.offAny?.(onAnyDevLog);
      };
    }
  }, [opts?.isTopApp, socket]);

  return { socket };
}

type SessionMessageMap = Record<HiSession['id'], Record<HiMessage['id'], HiMessage>>;
type HiChatLocalUser = { unreadMessageCount: number };
type PersistedChatDraftMap = Record<string, Record<HiSession['id'], string>>;
type HiChatScamMessageReadMap = Record<HiSession['id'], Record<HiMessage['id'], boolean>>;
type HiUserCache = Record<string, UserInfo | OfficialAccount | SystemAccount>;

const HiChatMessageStore = atom<SessionMessageMap>({
  key: 'HiChatMessage',
  default: {},
});

const HiChatLocalUserStore = atom<HiChatLocalUser>({
  key: 'HiChatLocalUser',
  default: {
    unreadMessageCount: 0,
  },
});
const HiUserCacheStore = atom<HiUserCache>({
  key: 'HiUserCache',
  default: {},
});

const HI_CHAT_DRAFT_KEY = 'hi_chat_draft';
const HiChatDraftStore = atom<PersistedChatDraftMap>({
  key: 'HiChatDraft',
  default: JSON.parse(localStorage.getItem(HI_CHAT_DRAFT_KEY) || '{}'),
});

const HI_CHAT_SCAM_MESSAGE_READ_KEY = 'hi_chat_scam_message_read';

const HiChatScamMessageReadMapStore = atom<HiChatScamMessageReadMap>({
  key: 'HiChatScamMessageRead',
  default: JSON.parse(localStorage.getItem(HI_CHAT_SCAM_MESSAGE_READ_KEY) || '{}'),
});

export function useHiChatLocalUserInfo() {
  const setHiChatLocalUserInfo = useSetRecoilState(HiChatLocalUserStore);
  const [hiChatLocalUserInfo] = useRecoilState(HiChatLocalUserStore);

  const putHiChatLocalUserInfo = (unreadMessageCount: number = 0) => {
    setHiChatLocalUserInfo({ unreadMessageCount });
  };

  return {
    putHiChatLocalUserInfo,
    hiChatLocalUserInfo,
  };
}

export function useOperateHiChatSessionMessages() {
  const setMessageMap = useSetRecoilState(HiChatMessageStore);

  const putMessageOnSession = useCallback(
    (session_id: HiSession['id'], messages: HiMessage[]) => {
      setMessageMap(messageMap => {
        const nextState = produce(messageMap, draft => {
          const sessionMessages = draft[session_id] || {};
          messages.forEach(message => {
            if (
              sessionMessages[message.id] &&
              sessionMessages[message.id].is_open &&
              !message.is_open
            ) {
              // 消息已经是打开状态，不再更新
              return;
            }
            sessionMessages[message.id] = message;
          });
          draft[session_id] = sessionMessages;
        });
        return nextState;
      });
    },
    [setMessageMap],
  );

  const clearSessionMessages = useCallback(
    (session_id: HiSession['id']) => {
      setMessageMap(messageMap => {
        return produce(messageMap, draft => {
          draft[session_id] = {};
        });
      });
    },
    [setMessageMap],
  );

  return {
    putMessageOnSession,
    clearSessionMessages,
  };
}

export function usePersistedSessionMessages(session_id?: HiSession['id']) {
  const [stores] = useRecoilState(HiChatMessageStore);

  const sessionMap = session_id ? stores[session_id] : null;
  return useMemo(() => {
    const messageList: HiMessage[] = [];
    // const messageListDesc: HiMessage[] = [];
    if (sessionMap) {
      Array.from(Object.values(sessionMap))
        .sort((a, b) => (a.create_at < b.create_at ? 1 : -1))
        .forEach(message => {
          messageList.push(message);
          //   messageListDesc.unshift(message);
        });
    }

    return {
      messageList,
      //   messageListDesc,
    };
  }, [sessionMap]);
}

export function useOperateHiChatDraft() {
  const setDraftMap = useSetRecoilState(HiChatDraftStore);
  const { address } = useCurrentAddressInfo();

  const putDraftOnSession = useCallback(
    (session_id: HiSession['id'], text: string) => {
      if (!address || !session_id) {
        return;
      }
      setDraftMap(draftMap => {
        const nextState = produce(draftMap, draft => {
          const myDraft = draft[address] || {};
          if (!myDraft[session_id] && !text) {
            return;
          }
          myDraft[session_id] = text;
          draft[address] = myDraft;
        });

        try {
          localStorage.setItem(HI_CHAT_DRAFT_KEY, JSON.stringify(nextState));
        } catch (e) {
          console.error('setDraftMap', e);
        }
        return nextState;
      });
    },
    [address, setDraftMap],
  );

  return {
    putDraftOnSession,
  };
}

export function usePersistedHiChatDraft(session_id?: HiSession['id']) {
  const [stores] = useRecoilState(HiChatDraftStore);
  const { address } = useCurrentAddressInfo();

  const draftMap = address ? stores[address] : null;
  return useMemo(() => {
    if (draftMap && session_id) {
      return draftMap[session_id] || '';
    }
    return '';
  }, [draftMap, session_id]);
}
export function usePersistedHiChatDraftSessionMap() {
  const [stores] = useRecoilState(HiChatDraftStore);
  const { address } = useCurrentAddressInfo();

  return useMemo(() => {
    const draftMap = address ? stores[address] : {};
    return draftMap || {};
  }, [address, stores]);
}

export function useHiUserCache() {
  const setUserDict = useSetRecoilState(HiUserCacheStore);
  const [userDict] = useRecoilState(HiUserCacheStore);
  const updateHiUserCache = (
    userMap: Record<string, UserInfo | OfficialAccount | SystemAccount>,
  ) => {
    setUserDict(prevDict => {
      return {
        ...prevDict,
        ...userMap,
      };
    });
  };
  return {
    userDict,
    updateHiUserCache,
  };
}

export function useHiChatScamMessageRead() {
  const setScamMessage = useSetRecoilState(HiChatScamMessageReadMapStore);
  const [hiChatScamMessageReadMap] = useRecoilState(HiChatScamMessageReadMapStore);

  const putScamMessageRead = useCallback(
    (sessionId: HiSession['id'], messageId: HiMessage['id']) => {
      if (!sessionId || !messageId) {
        return;
      }
      setScamMessage(sessionsMap => {
        const nextState = produce(sessionsMap, draft => {
          const messageMap = draft[sessionId] || {};

          messageMap[messageId] = true;
          draft[sessionId] = messageMap;
        });

        try {
          localStorage.setItem(HI_CHAT_SCAM_MESSAGE_READ_KEY, JSON.stringify(nextState));
        } catch (e) {
          console.error('setDraftMap', e);
        }
        return nextState;
      });
    },
    [setScamMessage],
  );

  const isScamMessageRead = (sessionId: HiSession['id'], messageId: HiMessage['id']) => {
    return Boolean(hiChatScamMessageReadMap[sessionId]?.[messageId]);
  };

  return {
    putScamMessageRead,
    isScamMessageRead,
  };
}
